import { Loader, ThemeProvider } from '@portal/ui-library';
import React, { Suspense, useEffect } from 'react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';

import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { deDE } from '@mui/x-date-pickers/locales';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigContextProvider } from 'contexts/ConfigContext/ConfigContext';
import de from 'date-fns/locale/de';
import { DigitalOrderPage } from 'pages/DigitalOrderPage';
import { DoneStep } from 'pages/DigitalOrderPage/subpages/DoneStep';
import { theme } from 'styles/theme/theme';
import { WizardRoutesEnum } from 'utils/actions';
import { Button } from '@mui/material';

const germanLocale = deDE.components.MuiLocalizationProvider.defaultProps.localeText;
const client = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false
    }
  }
});

// Component to handle the skip to content button logic
const SkipToContentButton = () => {
  const location = useLocation();
  const isDonePage = location.pathname.includes(WizardRoutesEnum.Done);

  const handleSkipToContent = () => {
    if (isDonePage) {
      // On done page, focus the file download box
      const downloadBoxRef = (window as any).fileDownloadBoxRef;

      if (downloadBoxRef?.current) {
        downloadBoxRef.current.focus();
        downloadBoxRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    } else {
      // On main page, focus the tariff section anchor
      const anchorRef = (window as any).tariffSectionAnchorRef;

      if (anchorRef?.current) {
        anchorRef.current.focus();
        anchorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  };

  return (
    <div className="slide-down-button fixed top-0 left-1/2 -translate-x-1/2 transition-transform duration-300 ease-in-out z-[1000] translate-y-[-100%] focus-within:translate-y-0">
      <Button
        id="skipToContent"
        size="large"
        variant="contained"
        className="font-bold"
        onClick={handleSkipToContent}
      >
        Zum Inhalt springen
      </Button>
    </div>
  );
};

function App() {
  useEffect(() => {
    ModuleRegistry.registerModules([AllCommunityModule]);
  }, []);

  return (
    <React.StrictMode>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={de}
        localeText={germanLocale}
      >
        <ThemeProvider theme={theme}>
          <Suspense fallback={<Loader />}>
            <QueryClientProvider client={client}>
              <ConfigContextProvider>
                <BrowserRouter>
                  <SkipToContentButton />

                  <Routes>
                    <Route path="/" element={<DigitalOrderPage />} />
                    <Route
                      path={`${WizardRoutesEnum.Done}/:applicationId`}
                      element={<DoneStep />}
                    />
                  </Routes>
                </BrowserRouter>
              </ConfigContextProvider>
            </QueryClientProvider>
          </Suspense>
        </ThemeProvider>
      </LocalizationProvider>
    </React.StrictMode>
  );
}

export default App;
